import {ApiError, browserLogout, browserRefresh, getMe, type BrowserTokens, type Me} from "../api/client";
import {bookingEntryStorageKey} from "./booking-entry-storage-key";
import {withSessionAccess} from "./access-lock";

const storageKey = "magistika.browser-session.v1";
const impersonationReturnPathKey = "magistika.impersonation-return-path.v1";

export type SessionSnapshot =
  | {status: "loading"}
  | {status: "anonymous"}
  | {status: "authenticated"; me: Me}
  | {status: "error"; code: string; requestId: string};

export interface SessionAPI {
  refresh(): Promise<BrowserTokens>;
  logout(): Promise<void>;
  me(accessToken: string): Promise<Me>;
}

const defaultAPI: SessionAPI = {refresh: browserRefresh, logout: browserLogout, me: getMe};

export interface SessionOwner {
  readonly epoch: number;
  readonly accountId: number;
  readonly impersonationId: string | undefined;
}

export class SessionOwnerChangedError extends Error {
  constructor() { super("session_owner_changed"); }
}

export class SessionStore {
  private snapshotValue: SessionSnapshot = {status: "loading"};
  private access: BrowserTokens | null = null;
  private ownerConfirmedAccess: BrowserTokens | null = null;
  private refreshInFlight: Promise<BrowserTokens> | null = null;
  private readonly listeners = new Set<() => void>();
  private ownerEpochValue = 0;

  constructor(
    private readonly api: SessionAPI = defaultAPI,
    private readonly storage: Storage = window.sessionStorage,
    private readonly fetchImpl: typeof fetch = window.fetch.bind(window),
  ) {}

  snapshot = (): SessionSnapshot => this.snapshotValue;
  ownerEpoch = (): number => this.ownerEpochValue;

  captureOwner(): SessionOwner {
    const snapshot = this.snapshotValue;
    if (snapshot.status !== "authenticated") throw new SessionOwnerChangedError();
    return {epoch: this.ownerEpochValue, accountId: snapshot.me.account_id, impersonationId: snapshot.me.impersonation?.impersonation_id};
  }

  isCurrentOwner(owner: SessionOwner): boolean {
    const snapshot = this.snapshotValue;
    return snapshot.status === "authenticated" && owner.epoch === this.ownerEpochValue &&
      owner.accountId === snapshot.me.account_id && owner.impersonationId === snapshot.me.impersonation?.impersonation_id;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async bootstrap(): Promise<void> {
    this.setSnapshot({status: "loading"});
    this.access ??= this.readAccess();
    try {
      await this.withAuthorizedAccess(() => this.loadMe());
    } catch (error) {
      this.handleBootstrapError(error);
    }
  }

  async accept(tokens: BrowserTokens): Promise<void> {
    this.storage.removeItem(bookingEntryStorageKey);
    this.ownerEpochValue++;
    this.setSnapshot({status: "loading"});
    this.persistAccess(tokens);
    await this.loadMe();
  }

  async beginImpersonation(tokens: BrowserTokens, returnPath: string): Promise<void> {
    if (!tokens.impersonation) throw new ApiError(500);
    this.storage.setItem(impersonationReturnPathKey, safeImpersonationReturnPath(returnPath));
    try {
      await this.accept(tokens);
      if (this.snapshotValue.status !== "authenticated" || !this.snapshotValue.me.impersonation) {
        throw new ApiError(500);
      }
    } catch (error) {
      this.clear();
      throw error;
    }
  }

  async endImpersonation(tokens: BrowserTokens): Promise<string> {
    if (tokens.impersonation) throw new ApiError(500);
    const returnPath = safeImpersonationReturnPath(this.storage.getItem(impersonationReturnPathKey) ?? "");
    try {
      await this.accept(tokens);
      if (this.snapshotValue.status !== "authenticated" || this.snapshotValue.me.impersonation) {
        throw new ApiError(500);
      }
    } catch (error) {
      this.clear();
      throw error;
    }
    this.storage.removeItem(impersonationReturnPathKey);
    return returnPath;
  }

  async logout(): Promise<void> {
    this.clear();
    await this.api.logout();
  }

  async reloadMe(): Promise<void> {
    await this.withAuthorizedAccess(() => this.loadMe());
  }

  clear(): void {
    this.clearState();
  }

  invalidate(): void {
    this.clearState();
  }

  async authorizedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const requestInput = typeof input === "string" ? new URL(input, window.location.href) : input;
    const request = new Request(requestInput, init);
    return this.withAuthorizedAccess(() => this.fetchAuthorized(request), response => response.status === 401);
  }

  // Opt-in fence for private owner commands. In particular, refresh must never
  // retry A's body/key with B's current token. Existing callers keep their API.
  async authorizedOwnerFetch(owner: SessionOwner, input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const request = new Request(typeof input === "string" ? new URL(input, window.location.href) : input, init);
    const guard = () => {
      if (!this.isCurrentOwner(owner) || request.signal.aborted) throw new SessionOwnerChangedError();
    };
    guard();
    return this.withAuthorizedAccess(async () => {
      guard();
      const confirmedAccess = await this.confirmOwnerAccess(owner);
      guard();
      if (this.access !== confirmedAccess) throw new SessionOwnerChangedError();
      const response = await this.fetchAuthorized(request);
      guard();
      if (this.access !== confirmedAccess) throw new SessionOwnerChangedError();
      return response;
    }, response => response.status === 401);
  }

  // A rotated cookie can return another Account's bearer without a local accept.
  // Confirm this exact bearer through /me before any captured owner continues.
  async authorizedOwnerCommandOnce(owner: SessionOwner, input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const request = new Request(typeof input === "string" ? new URL(input, window.location.href) : input, init);
    const guard = () => {
      if (!this.isCurrentOwner(owner) || request.signal.aborted) throw new SessionOwnerChangedError();
    };
    guard();
    return this.withAuthorizedAccess(async () => {
      guard();
      const confirmed = await this.confirmOwnerAccess(owner);
      guard();
      if (this.access !== confirmed) throw new SessionOwnerChangedError();
      const response = await this.fetchAuthorized(request);
      guard();
      if (this.access !== confirmed) throw new SessionOwnerChangedError();
      // A deletion may already have revoked this session. Recovery is a separate
      // anonymous status GET; never refresh/replay a contact-bearing command.
      return response;
    });
  }

  private async withAuthorizedAccess<T>(task: () => Promise<T>, unauthorized: (value: T) => boolean = () => false): Promise<T> {
    const epoch = this.ownerEpochValue;
    let attempted: BrowserTokens | null = null;
    const guard = () => { if (epoch !== this.ownerEpochValue) throw new SessionOwnerChangedError(); };
    const first = await withSessionAccess("shared", async () => {
      guard();
      attempted = this.access;
      if (!attempted || isExpiring(attempted.access_expires_at)) return {retry: true} as const;
      try {
        const value = await task();
        return unauthorized(value) ? {retry: true} as const : {retry: false, value} as const;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return {retry: true} as const;
        throw error;
      }
    });
    if (!first.retry) return first.value;
    return withSessionAccess("exclusive", async () => {
      guard();
      try {
        // Another request in this tab may have renewed while we waited. Its
        // bearer is already current; a late 401 must not rotate it again.
        if (!this.access || this.access === attempted || isExpiring(this.access.access_expires_at)) await this.refresh();
        guard();
        const value = await task();
        guard();
        if (unauthorized(value)) this.clear();
        return value;
      } catch (error) {
        if (epoch === this.ownerEpochValue && error instanceof ApiError && error.status === 401) this.clear();
        throw error;
      }
    });
  }

  private async confirmOwnerAccess(owner: SessionOwner): Promise<BrowserTokens> {
    const access = this.access;
    if (!access) throw new SessionOwnerChangedError();
    if (this.ownerConfirmedAccess === access) return access;
    try {
      await this.loadMe();
      if (this.access !== access) throw new SessionOwnerChangedError();
      return access;
    } catch (error) {
      // A peer tab may have rotated since this bearer was saved. The access
      // coordinator retries /me with a fresh bearer before invalidating owner.
      if (error instanceof ApiError && error.status === 401) throw error;
      // Preserve an independently accepted owner; otherwise the current bearer
      // is unconfirmed and cannot be retried with the old snapshot or selection.
      if (this.isCurrentOwner(owner)) {
        this.clear();
        this.handleBootstrapError(error);
      }
      throw error;
    }
  }

  private async fetchAuthorized(request: Request): Promise<Response> {
    const retryable = request.clone();
    const headers = new Headers(retryable.headers);
    if (this.access) headers.set("Authorization", `Bearer ${this.access.access_token}`);
    return this.fetchImpl(new Request(retryable, {headers, credentials: "same-origin"}));
  }

  private async refresh(): Promise<BrowserTokens> {
    if (!this.refreshInFlight) {
      const epoch = this.ownerEpochValue;
      this.refreshInFlight = this.api.refresh().then((tokens) => {
        if (epoch !== this.ownerEpochValue) throw new SessionOwnerChangedError();
        this.persistAccess(tokens);
        return tokens;
      }).finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async loadMe(): Promise<void> {
    const access = this.access;
    if (!access) throw new ApiError(401);
    const epoch = this.ownerEpochValue;
    const me = await this.api.me(access.access_token);
    if (epoch !== this.ownerEpochValue) throw new SessionOwnerChangedError();
    const previous = this.snapshotValue;
    if (previous.status === "authenticated" && (previous.me.account_id !== me.account_id || previous.me.impersonation?.impersonation_id !== me.impersonation?.impersonation_id)) {
      this.ownerEpochValue++;
      this.storage.removeItem(bookingEntryStorageKey);
    }
    if (this.access === access) this.ownerConfirmedAccess = access;
    this.setSnapshot({status: "authenticated", me});
  }

  private handleBootstrapError(error: unknown): void {
    if (error instanceof SessionOwnerChangedError) return;
    if (error instanceof ApiError && error.status === 401) {
      this.clear();
      return;
    }
    this.setSnapshot({
      status: "error",
      code: error instanceof ApiError ? error.code : "client_error",
      requestId: error instanceof ApiError ? error.requestId : "",
    });
  }

  private persistAccess(tokens: BrowserTokens): void {
    this.access = tokens;
    this.ownerConfirmedAccess = null;
    this.storage.setItem(storageKey, JSON.stringify(tokens));
  }

  private readAccess(): BrowserTokens | null {
    const raw = this.storage.getItem(storageKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as BrowserTokens;
      return parsed.access_token && parsed.access_expires_at ? parsed : null;
    } catch {
      this.storage.removeItem(storageKey);
      return null;
    }
  }

  private clearState(): void {
    this.ownerEpochValue++;
    this.access = null;
    this.ownerConfirmedAccess = null;
    this.storage.removeItem(storageKey);
    this.storage.removeItem(impersonationReturnPathKey);
    this.storage.removeItem(bookingEntryStorageKey);
    this.setSnapshot({status: "anonymous"});
  }

  private setSnapshot(next: SessionSnapshot): void {
    this.snapshotValue = next;
    this.listeners.forEach((listener) => listener());
  }
}

function safeImpersonationReturnPath(value: string): string {
  return /^\/app\/admin\/users\/\d+\/?$/.test(value) ? value : "/app/admin/users";
}

function isExpiring(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() <= Date.now() + 30_000;
}

export const sessionStore = new SessionStore();
